import { ApolloError } from '@apollo/client';
import { useCallback, useMemo, useState } from 'react';
import { isNullish } from 'remeda';
import { useLandingPageConfig, useSaveLandingPageConfig } from 'src/graphql';
import { type LandingPageFormValues } from 'src/pages/Settings/memberships/components/landing-page-tab/types';
import type { LiveLinkLookup } from 'src/pages/Settings/memberships/hooks/use-live-link-state';
import { useRefetchSegmentBaseUrl } from 'src/pages/Settings/memberships/hooks/use-segment-base-url';
import {
  type ConfigFormSaveRequest,
  type ConfigFormSubmitState,
  type ConfigSurfaceController,
  EMPTY_CONFIG_FORM_SUBMIT_STATE,
} from 'src/pages/Settings/memberships/types';
import { mapLandingPageConfigToFormValues } from 'src/pages/Settings/memberships/utils/map-landing-page-config-to-form-values';
import { parseLandingPageError } from 'src/pages/Settings/memberships/utils/parse-landing-page-error';
import { serializeLandingPageConfig } from 'src/pages/Settings/memberships/utils/serialize-landing-page-config';
import { useConfirmationModal } from 'src/utils/hooks';
import { useNotifications } from 'src/utils/hooks/use-notifications';

const SAVE_FAILED_MESSAGE = 'Failed to save the landing page.';

interface UseLandingPageSurfaceParams {
  partnerId: number | null;
  selectedSegmentId: string | null;
  canManage: boolean;
}

interface LandingPageSurface extends ConfigSurfaceController<LandingPageFormValues> {
  formKey: string;
  data: LandingPageFormValues;
  liveFormValues: LandingPageFormValues | null;
  onFormValuesChange: (values: LandingPageFormValues) => void;
  /** The raw stored blob, so the preview can serialize exactly what a save would write. */
  storedConfig: string | null | undefined;
  /** The stored page's query, so the live link can tell a failed load from an unpublished page. */
  configLookup: LiveLinkLookup;
}

export const useLandingPageSurface = ({
  partnerId,
  selectedSegmentId,
  canManage,
}: UseLandingPageSurfaceParams): LandingPageSurface => {
  const { alert } = useConfirmationModal();
  const { showErrorAlert, showSuccessAlert } = useNotifications();
  const { config, loading, error, refetch } = useLandingPageConfig(
    { partnerId, brandId: selectedSegmentId },
    { skip: !canManage },
  );
  const { saveLandingPageConfig, loading: saveLoading } = useSaveLandingPageConfig();
  const refetchSegmentBaseUrl = useRefetchSegmentBaseUrl();

  const [requestSaveValues, setRequestSaveValues] =
    useState<ConfigFormSaveRequest<LandingPageFormValues> | null>(null);
  const [submitState, setSubmitState] = useState<ConfigFormSubmitState>(
    EMPTY_CONFIG_FORM_SUBMIT_STATE,
  );
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [liveFormValues, setLiveFormValues] = useState<LandingPageFormValues | null>(null);

  const data = useMemo<LandingPageFormValues>(
    () => mapLandingPageConfigToFormValues(config),
    [config],
  );
  const hasUnsavedChanges = Boolean(requestSaveValues) && isFormDirty;
  const isSaving = submitState.isSubmitting || saveLoading;

  const handleSave = useCallback(async () => {
    if (!canManage || !requestSaveValues) {
      return;
    }

    if (!selectedSegmentId || isNullish(partnerId)) {
      await alert({
        title: 'Select a segment first',
        body: 'Pick a partner and client segment before saving.',
        acknowledgeLabel: 'Got it',
      });
      return;
    }

    const savePayload = await requestSaveValues();

    if (!savePayload) {
      await alert({
        title: 'Fix the highlighted fields',
        body: 'Some fields are invalid, so nothing was saved.',
        acknowledgeLabel: 'Got it',
      });
      return;
    }

    const { values, reset } = savePayload;

    const nextConfig = serializeLandingPageConfig({
      storedConfig: config?.config,
      blocks: values.blocks,
      settings: values.settings,
    });

    try {
      const response = await saveLandingPageConfig({
        variables: {
          input: {
            partnerId,
            brandId: selectedSegmentId,
            name: values.name,
            slug: values.slug,
            config: nextConfig,
          },
        },
      });

      if (!response.data?.saveLandingPageConfig?.success) {
        showErrorAlert(SAVE_FAILED_MESSAGE);
        return;
      }

      reset();
      showSuccessAlert('Landing page saved.');
      void refetchSegmentBaseUrl();
      await refetch();
    } catch (saveError) {
      showErrorAlert(
        saveError instanceof ApolloError
          ? parseLandingPageError(saveError)
          : SAVE_FAILED_MESSAGE,
      );
    }
  }, [
    canManage,
    alert,
    config,
    partnerId,
    refetch,
    refetchSegmentBaseUrl,
    requestSaveValues,
    saveLandingPageConfig,
    selectedSegmentId,
    showErrorAlert,
    showSuccessAlert,
  ]);

  const saveSlots = useMemo(
    () => ({
      onSaveRequestChange: setRequestSaveValues,
      onDirtyChange: setIsFormDirty,
      onSubmitStateChange: setSubmitState,
    }),
    [],
  );

  return {
    formKey: `${String(partnerId)}:${selectedSegmentId ?? ''}`,
    data,
    liveFormValues,
    onFormValuesChange: setLiveFormValues,
    storedConfig: config?.config,
    configLookup: { loading, error, refetch },
    saveSlots,
    hasUnsavedChanges,
    isSaving,
    // Not gated on validity: a segment's first page starts without a name or slug, and saving is
    // what reveals those fields on the Settings tab.
    isSaveDisabled: !canManage || isSaving,
    handleSave,
  };
};
